// Scope-iterating damage ops. Pure pickers + dispatch to the host's
// damageTarget primitive. Used by aura on_turn_end triggers like
// Thunderstorm ("deal 3 damage to a random unit atop affected sites").
//
// Host adapter contract (subset of the silent-ops host):
//   resolveScope(sourceCard, scope, args): card[]
//   damageTarget(card, amount, opts): void
//   logAction(key, values?): void
//   rng(): number. Uniform [0, 1) RNG; injected so deterministic
//                     under a seeded session.

import { msg } from '../../../logMessage';

/**
 * Pick one random card from the entry's scope matching `each` filter
 * and damage it by `amount`. No-op (with a log line) when the scope is
 * empty so the card text matches observed behavior.
 *
 * @param {object} sourceCard
 * @param {object} entry - the parent automation entry (provides scope)
 * @param {object} args  - { amount, each: { type?, subtype?, scope?, exclude_self? } }
 * @param {object} host
 */
export function applyDamageRandomInScope(sourceCard, entry, args, host) {
  const each = { ...(args?.each || {}) };
  const scope = each.scope ? { where: each.scope } : entry.scope;
  const matches = host.resolveScope(sourceCard, scope, {
    filter: each,
    exclude_self: each.exclude_self !== false,
  });
  if (matches.length === 0) {
    host.logAction('{source}: no targets in scope', { source: sourceCard.name || msg('Source') });
    return;
  }
  const rng = typeof host.rng === 'function' ? host.rng : Math.random;
  const idx = Math.floor(rng() * matches.length);
  const target = matches[idx];
  if (!target) return;
  const amount = args?.amount ?? 1;
  host.damageTarget(target, amount, {
    sourceLabel: sourceCard.name || 'Source',
    sourceId: sourceCard.id ?? sourceCard.instanceId,
    fx: args?.fx,
    variant: args?.variant,
    log: true,
    sweep: true,
    floatingDamage: true,
  });
}
